import { LoadingSkeletons } from '@/components/LoadingSkeletons/LoadingSkeletons';
import NoResults from '@/components/NoSearchResults/NoResults';
import { CAMPAIGN_CONTAINER_MAX_WIDTH, CAMPAIGN_CONTAINER_MIN_WIDTH, SKELETON_COUNT } from '@/pages/campaign/constants';
import { useGetCampaigns } from '@/pages/campaign/ListPage/api';
import { useAuth } from '@/services/auth';
import { campaignsSearch } from '@/state/campaigns/state';
import { CampaignAccess, ICampaign } from '@d-id/types';
import styled from '@emotion/styled';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useRecoilState } from 'recoil';
import { AssetsModal } from '../Modals';
import { CreditsCalculatorModal } from '../Modals/CreditsCalculator/CreditsCalculatorModal';
import { CampaignsList } from './CampaignsList';
import { FirstCampaignBanner } from './FirstCampaignBanner';

const EndOfPage = forwardRef<HTMLDivElement, { show: boolean }>(({ show }, ref) => {
    if (!show) {
        return null;
    }

    return (
        <div ref={ref}>
            <LoadingSkeletons
                count={SKELETON_COUNT}
                skeletonMaxWidth={CAMPAIGN_CONTAINER_MAX_WIDTH}
                containerMinWidth={CAMPAIGN_CONTAINER_MIN_WIDTH}
            />
        </div>
    );
});

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

export const CampaignsLibrary = () => {
    const { isAnonymous } = useAuth();
    const { data, fetchNextPage, isLoading, hasNextPage } = useGetCampaigns(isAnonymous);
    const { ref, inView: endOfListInView } = useInView();
    const [search, setSearch] = useRecoilState(campaignsSearch);
    const [openAssetsModal, setOpenAssetsModal] = useState<ICampaign | null>(null);
    const [openCreditsCalculatorModal, setOpenCreditsCalculatorModal] = useState<ICampaign | null>(null);
    const [showCreditsCalculatorBackButton, setShowCreditsCalculatorBackButton] = useState(false);

    useEffect(() => {
        if (endOfListInView && !isLoading) {
            fetchNextPage();
        }
    }, [endOfListInView, isLoading]);

    const filteredCampaigns = useMemo(
        () => data.filter(data => !search.trim() || data.title.toLowerCase().includes(search.toLowerCase())),
        [data, search]
    );

    const publishedCampaigns = useMemo(
        () => filteredCampaigns.filter(campaign => campaign.access === CampaignAccess.PUBLIC),
        [filteredCampaigns]
    );
    const unpublishedCampaigns = useMemo(
        () => filteredCampaigns.filter(campaign => campaign.access === CampaignAccess.PRIVATE),
        [filteredCampaigns]
    );

    if (isLoading) {
        return (
            <LoadingSkeletons
                count={SKELETON_COUNT}
                skeletonMaxWidth={CAMPAIGN_CONTAINER_MAX_WIDTH}
                containerMinWidth={CAMPAIGN_CONTAINER_MIN_WIDTH}
            />
        );
    }

    if (!data.length) {
        return <FirstCampaignBanner />;
    }

    if (!filteredCampaigns.length) {
        return <NoResults show handleClearSearch={() => setSearch('')} />;
    }

    return (
        <Container>
            <CampaignsList
                campaigns={publishedCampaigns}
                title="Published campaigns"
                openAssetsModal={(campaign: ICampaign) => setOpenAssetsModal(campaign)}
                openCreditsCalculatorModal={(campaign: ICampaign) => setOpenCreditsCalculatorModal(campaign)}
            />
            <CampaignsList
                campaigns={unpublishedCampaigns}
                title="Unpublished campaigns"
                openAssetsModal={(campaign: ICampaign) => setOpenAssetsModal(campaign)}
                openCreditsCalculatorModal={(campaign: ICampaign) => setOpenCreditsCalculatorModal(campaign)}
            />
            <EndOfPage ref={ref} show={!!hasNextPage} />

            <AssetsModal campaign={openAssetsModal} open={!!openAssetsModal} onClose={() => setOpenAssetsModal(null)} />
            <CreditsCalculatorModal
                campaign={openCreditsCalculatorModal}
                open={!!openCreditsCalculatorModal}
                onClose={() => {
                    setShowCreditsCalculatorBackButton(false);
                    setOpenCreditsCalculatorModal(null);
                }}
                showBackButton={showCreditsCalculatorBackButton}
                onClickBack={() => {
                    setOpenCreditsCalculatorModal(null);
                    setOpenAssetsModal(openCreditsCalculatorModal);
                    setShowCreditsCalculatorBackButton(false);
                }}
            />
        </Container>
    );
};
